import {
  PlanCardButtonProps,
  PlanCardProps,
} from '@ww-digital/web-palette-react/dist/components/Price/PlanCard/PlanCard';
import { OfferBundle, OfferBundleOfferPlan } from '../../../hooks/useProducts';
import { AnalyticsUtility } from '../../Utility/AnalyticsUtility.ts';
import { EntitlementContextType } from '../../../context/entitlement.context.ts';
import { OfferPlanContents } from '../../Utility/ProductsUtility.ts';
import { QueryStringContextType } from '../../../context/querystring.context.ts';

type PlanSliceType = {
  [key: string]: number;
};

const planTypes = { points: 0, workshop: 1, clinic: 2 } as PlanSliceType;

export const getRecommendedIndexByPlanType = (type: string): number => {
  return planTypes[type] ?? -1;
};

export const getContentByKey = (
  contents: { contentKey?: string; contentValue?: string }[] | undefined,
  key: string,
): string => {
  if (!contents || !Array.isArray(contents) || !key) return '';
  const normalized = key.toUpperCase();
  const found = contents.find(
    (item) => item?.contentKey?.toUpperCase() === normalized,
  );
  return found?.contentValue ?? '';
};

const convertHtmlToPlainText = (htmlString?: string): string =>
  AnalyticsUtility.toPlainText(htmlString || '');

const parseJsonData = (value?: string, returnRaw = false): any => {
  if (typeof value !== 'string') return returnRaw ? value : null;
  try {
    return JSON.parse(value);
  } catch {
    return returnRaw ? value : null;
  }
};

export const getContentObjectByKey = (
  offerBundle: OfferBundle | undefined,
  key: string,
): any => {
  const contentValue = getContentByKey(offerBundle?.contents as any, key);
  return parseJsonData(contentValue, true);
};

export const getFeaturesList = (offerBundle: OfferBundle): string[] => {
  const features = getContentObjectByKey(offerBundle, 'FEATURES_DESCRIPTION');
  if (!Array.isArray(features)) return [];
  return features
    .filter((f: any) => f && f.included)
    .map((f: any) => f.text || '')
    .filter(Boolean);
};

const getDaDuration = (duration = ''): string =>
  duration ? duration.replace(/\W/g, '').toLowerCase() : '';

const getDALabel = (plan: string, duration = '') => {
  const label = duration ? `${plan}_${getDaDuration(duration)}` : plan;
  return AnalyticsUtility.formatLabel(label);
};

const getDaPlanName = (planName = ''): string => {
  const stNames: Record<string, string> = {
    CORE: 'digital',
    PREMIUM: 'workshops',
    PERSONAL_COACHING: 'coaching',
    CLINICAL: 'clinical',
  };
  return stNames[planName.toUpperCase()] ?? planName;
};

const getDACategory = (
  entitlement: EntitlementContextType['entitlement'],
  suffix: string,
) => {
  return AnalyticsUtility.formatCategory(entitlement, `dfpricing_${suffix}`);
};

export const getContentAsDictionary = (
  contents?: OfferPlanContents[],
): Record<string, string> => {
  if (!contents) {
    return {};
  }

  return contents.reduce((acc: Record<string, string>, con) => {
    if (con?.contentKey) acc[con.contentKey] = con.contentValue ?? '';
    return acc;
  }, {});
};

export const buildUrl = (
  offerPlan: OfferBundleOfferPlan,
  ownerId: string | null,
  offerBundleId: string | undefined,
): string => {
  const rawPath =
    typeof window !== 'undefined' ? window.location.pathname : '/';
  const returnPath = rawPath.replace('us/', '');
  const params = new URLSearchParams({
    op: offerPlan.offerPlanId,
    own: ownerId ?? '',
    ob: offerBundleId ?? '',
    returnPath: returnPath,
  });
  return `/us/signup/a/user-account?${params.toString()}`;
};

export const getPlanPrice = (
  queryArgs: QueryStringContextType,
  price: string,
  entitlement: EntitlementContextType['entitlement'],
  planData: OfferBundleOfferPlan,
  buttonText: string,
  secondaryButtonText?: string,
  offerBundle?: OfferBundle,
) => {
  if (!planData || !offerBundle) return {};

  const baseUrl = buildUrl(
    planData,
    offerBundle?.ownerId,
    offerBundle?.offerBundleId,
  );
  const url = queryArgs.iafToken
    ? `${baseUrl}&iafToken=${encodeURIComponent(queryArgs.iafToken)}&iafReferrerOC=${encodeURIComponent(
        queryArgs.iafReferrerOC || '',
      )}&iafVariant=${encodeURIComponent(queryArgs.iafVariant || '')}`
    : baseUrl;

  const label = getContentByKey(
    planData?.offerPlanData?.contents as any,
    'SALES_PITCH',
  );

  const daPlanName = getDaPlanName(offerBundle.subscriptionType);
  const daCategory = getDACategory(entitlement, daPlanName);
  const daLabel = getDALabel(
    daPlanName,
    getContentByKey(
      planData?.offerPlanData?.contents as any,
      'OFFER_PLAN_NAME',
    ),
  );

  const button: any = {
    text: buttonText,
    url,
    attributes: {
      'da-category': daCategory,
      'da-label': daLabel,
      'da-action': 'continue',
    },
  };
  if (secondaryButtonText) button.secondaryText = secondaryButtonText;

  return {
    priceText: label,
    price,
    button,
  };
};

const getPlanOfferData = (
  offerPlan: OfferBundleOfferPlan,
  offerBundle: OfferBundle,
  ownerId: string,
  entitlement: EntitlementContextType['entitlement'],
): PlanCardButtonProps => {
  const planData = offerPlan?.offerPlanData;
  const offerTerms = getContentByKey(planData?.contents, 'PROMOTION_CONTENT');
  const basePlan = planData?.plan?.basePlan;
  const intervalUnit =
    basePlan?.totalBillingCycles === 1
      ? basePlan?.intervalUnit === 'months'
        ? 'month'
        : basePlan?.intervalUnit
      : basePlan?.intervalUnit;
  const link = buildUrl(offerPlan, ownerId, offerBundle.offerBundleId);
  const text = `Get ${basePlan?.totalBillingCycles} ${intervalUnit} for $${planData?.plan?.salePrice}/mo`;

  const daPlanName = getDaPlanName(offerBundle.subscriptionType);
  const daCategory = getDACategory(entitlement, daPlanName);
  const daLabel = getDALabel(
    daPlanName,
    getContentByKey(planData?.contents, 'OFFER_PLAN_NAME'),
  );
  const buttonAttributes = {
    'da-category': daCategory,
    'da-label': daLabel,
    'da-action': 'continue',
  };

  return {
    offerTerms,
    link,
    text,
    attributes: buttonAttributes,
  };
};

const mapOfferPlansToPlan = (
  offerPlans: OfferBundleOfferPlan[],
  offerBundle: OfferBundle,
  ownerId: string,
  entitlement: EntitlementContextType['entitlement'],
): PlanCardProps => {
  const { bestOfferPlanId } = offerBundle;

  const bestOfferPlan =
    offerPlans.find((t) => t.offerPlanId === bestOfferPlanId) || offerPlans[0];

  const bestOffer = getPlanOfferData(
    bestOfferPlan,
    offerBundle,
    ownerId,
    entitlement,
  );

  const filteredPlans = offerPlans.filter((t) => t !== bestOfferPlan);
  const moreOptions = filteredPlans.map((t) => {
    return getPlanOfferData(t, offerBundle, ownerId, entitlement);
  });

  const headerText = getContentByKey(offerBundle.contents, 'TITLE');
  const subHeaderText = convertHtmlToPlainText(
    getContentByKey(offerBundle.contents, 'DESCRIPTION'),
  );

  bestOffer.tag = !bestOfferPlanId
    ? ''
    : getContentByKey(bestOfferPlan?.offerPlanData?.contents, 'SALES_PITCH') ||
      'Best Value';

  return {
    ...bestOffer,
    headerText,
    subHeaderText,
    items: [],
    checkListMinHeight: '268px',
    mainButton: bestOffer,
    asteriskText:
      offerBundle.subscriptionType === 'PREMIUM'
        ? 'Offer not available in all areas.'
        : '*Copays, coinsurance & deductibles may apply. Additional fee required without insurance coverage.',
    hasMoreOptions: moreOptions.length > 0,
    moreOptions: moreOptions,
  };
};

export const getMappedPlan = (
  offerBundle: OfferBundle,
  ownerId: string,
  entitlement: EntitlementContextType['entitlement'],
): PlanCardProps => {
  if (!offerBundle?.offerPlans?.length) {
    return {};
  }
  return mapOfferPlansToPlan(
    offerBundle.offerPlans,
    offerBundle,
    ownerId,
    entitlement,
  );
};
